import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

import { COLORS, PROP_DEFINITIONS } from "./dioramaLayout";
import { InteractiveProp } from "./InteractiveProp";

const DEF = PROP_DEFINITIONS.find((p) => p.id === "computer")!;

/** Visual-only CRT computer — reusable outside the engine's InteractiveProp wrapper. */
export function ComputerModel({ isNight = false }: { isNight?: boolean }) {
  const lightRef = useRef<THREE.PointLight>(null);
  const screenTimeRef = useRef(0);

  // At night the CRT becomes a real light source: stronger emissive screen
  // plus a brighter, wider point light so it casts glow onto nearby sand.
  const glowBase = isNight ? 1.0 : 0.4;
  const glowAmp = isNight ? 0.25 : 0.1;

  const { canvas, texture } = useMemo(() => {
    const c = document.createElement("canvas");
    c.width = 128;
    c.height = 96;
    const t = new THREE.CanvasTexture(c);
    t.minFilter = THREE.NearestFilter;
    t.magFilter = THREE.NearestFilter;
    return { canvas: c, texture: t };
  }, []);

  useFrame((_, delta) => {
    screenTimeRef.current += delta;
    const t = screenTimeRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Background
    ctx.fillStyle = COLORS.screenBg;
    ctx.fillRect(0, 0, 128, 96);

    // Scanlines
    ctx.fillStyle = "rgba(168,230,207,0.03)";
    for (let y = 0; y < 96; y += 2) {
      ctx.fillRect(0, y, 128, 1);
    }

    // Text lines
    for (let i = 0; i < 6; i++) {
      const lineProgress = ((t * 0.8 + i * 1.5) % 8) / 8;
      const charCount = Math.floor(lineProgress * 20);
      const y = 12 + i * 12;

      // Prompt symbol
      ctx.fillStyle = "#F28B82";
      ctx.fillRect(4, y, 3, 6);

      // Text blocks
      ctx.fillStyle = COLORS.screenGlow;
      let x = 10;
      for (let c = 0; c < charCount; c++) {
        const w = 2 + Math.floor(Math.sin(c * 3 + i) * 1.5 + 2);
        ctx.fillRect(x, y, w, 5);
        x += w + 2;
        if (x > 120) break;
      }
    }

    // Blinking cursor
    if (Math.sin(t * 4) > 0) {
      ctx.fillStyle = COLORS.screenGlow;
      ctx.fillRect(10 + Math.floor((t * 8) % 80), 72, 4, 7);
    }

    // Progress bar
    ctx.fillStyle = COLORS.tealDark;
    ctx.fillRect(8, 86, 112, 6);
    ctx.fillStyle = COLORS.screenGlow;
    const progress = ((t * 0.3) % 1) * 112;
    ctx.fillRect(8, 86, progress, 6);

    // Fish sprite
    const fishX = (t * 15) % 140 - 10;
    ctx.fillStyle = "#F7CE68";
    ctx.beginPath();
    ctx.moveTo(fishX, 50);
    ctx.lineTo(fishX - 6, 47);
    ctx.lineTo(fishX - 6, 53);
    ctx.fill();
    ctx.fillRect(fishX - 4, 48, 6, 4);

    texture.needsUpdate = true;

    // Pulse screen glow
    if (lightRef.current) {
      lightRef.current.intensity = glowBase + Math.sin(t * 2) * glowAmp;
    }
  });

  // Keyboard keys
  const keys = useMemo(() => {
    const result: Array<{ id: string; position: [number, number, number] }> = [];
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 8; col++) {
        result.push({
          id: `key_${row}_${col}`,
          position: [-0.5 + col * 0.14, 0.1, -0.13 + row * 0.14],
        });
      }
    }
    return result;
  }, []);

  return (
    <group>
      {/* Monitor body */}
      <mesh position={[0, 0.65, 0]} castShadow>
        <boxGeometry args={[1.6, 1.3, 1.0]} />
        <meshStandardMaterial color={COLORS.cream} flatShading />
      </mesh>

      {/* Monitor bezel */}
      <mesh position={[0, 0.7, 0.53]}>
        <boxGeometry args={[1.3, 1.0, 0.05]} />
        <meshStandardMaterial color="#333333" flatShading />
      </mesh>

      {/* Screen */}
      <mesh position={[0, 0.7, 0.56]}>
        <planeGeometry args={[1.2, 0.9]} />
        <meshStandardMaterial
          map={texture}
          emissive="#ffffff"
          emissiveMap={texture}
          emissiveIntensity={isNight ? 1.2 : 0.5}
          flatShading
        />
      </mesh>

      {/* Screen glow */}
      <pointLight
        ref={lightRef}
        position={[0, 0.8, 1.2]}
        color={COLORS.screenGlow}
        intensity={glowBase}
        distance={isNight ? 4.5 : 3}
      />

      {/* Monitor base */}
      <mesh position={[0, 0.075, 0]} castShadow>
        <boxGeometry args={[0.8, 0.15, 0.6]} />
        <meshStandardMaterial color={COLORS.cream} flatShading />
      </mesh>

      {/* Keyboard */}
      <group position={[0, 0, 1.2]}>
        <mesh position={[0, 0.04, 0]} castShadow>
          <boxGeometry args={[1.4, 0.08, 0.5]} />
          <meshStandardMaterial color={COLORS.cream} flatShading />
        </mesh>
        {keys.map((key) => (
          <mesh key={key.id} position={key.position}>
            <boxGeometry args={[0.12, 0.04, 0.1]} />
            <meshStandardMaterial color="#DDDDDD" flatShading />
          </mesh>
        ))}
      </group>
    </group>
  );
}

export function OfficeComputer({ isNight = false }: { isNight?: boolean }) {
  return (
    <InteractiveProp
      propId={DEF.id}
      label={DEF.label}
      clickMessage={DEF.clickMessage}
      position={DEF.position}
    >
      <ComputerModel isNight={isNight} />
    </InteractiveProp>
  );
}
